import { Box, Button, ButtonIcon, IconDeleteOutline, Input } from '@capitalise/ui-design-system';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { email as emailValidator } from 'utils/Validators';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutationPostUserExists } from 'services/auth/queries';
import InviteTeamBenefits from './InviteTeamBenefits';
import { useModalContext } from 'hooks/useModalContext';
import useEmailList from './useEmailList';

const FullWidthForm = styled.form`
  width: 100%;
`;

const schema = yup.object().shape({
  email: yup.string().email().required(),
});

export interface IInviteTeamProps {
  showBenefits?: boolean;
}

const InviteTeam = ({ showBenefits = false }: IInviteTeamProps) => {
  const { emailList, setEmailList } = useEmailList([]);
  const { metaData, setMetaData } = useModalContext();

  const {
    register,
    handleSubmit,
    formState: { isValid, isSubmitting },
    getValues,
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    }
  });

  useEffect(() => {
    const disable = emailList.length === 0;

    setMetaData({
      ...metaData,
      'Invite Team': { disableNextButton: disable },
      inviteEmails: emailList,
    });
  }, [emailList]);

  const [error, setError]: [string, Function] = useState(null);

  const { mutateAsync: checkUserExists } = useMutationPostUserExists();

  const debouncedValidateEmail = useDebouncedCallback((event: KeyboardEvent<HTMLInputElement>) => {
    const emailTyped = (event.target as HTMLInputElement).value;

    const hasError = emailValidator()(emailTyped);

    setError(hasError);
  }, 350);

  const onSubmit = async () => {
    const newEmail = getValues('email');

    if (emailList.includes(newEmail)) {
      setError('Email already added to the list');
      return;
    }

    let checkEmail;

    try {
      // eslint-disable-next-line
      checkEmail = await checkUserExists(newEmail);
    } catch (error) {
      setError('Unable to check if email already existed. Please try again');
      return;
    }

    const emailAlreadyExist = checkEmail?.[0]?.[newEmail] || false;

    if (emailAlreadyExist) {
      setError('The user already exists and can not be added');
      return;
    }

    setEmailList(emailList.concat(getValues('email')));

    reset();
    setError(null);
  };

  return (
    <>
      <FullWidthForm onSubmit={handleSubmit(onSubmit)}>
        {showBenefits && (
          <Box pb="xsmall">
            <InviteTeamBenefits />
          </Box>
        )}
        <Box display="flex" width="100%">
          <Box flex="1">
            <Input
              {...register('email')}
              error={error}
              label=""
              fullWidth
              type="email"
              placeholder="e.g. john.doe@gmail.com"
              onInput={debouncedValidateEmail}
              data-testId="invite-team-members-input"
            />
          </Box>
          <Box pl="3xsmall">
            <Button
              variant="outlined"
              baseColor="green"
              disabled={!isValid}
              type="submit"
              loading={isSubmitting}
            >
              Invite
            </Button>
          </Box>
        </Box>
      </FullWidthForm>

      {emailList.length > 0 && (
        <Box display="flex" width="100%" pt="xsmall" flexDirection="column" ref={listRef}>
          <Box display="flex" width="100%" alignItems="center">
            <Box typography="heading-16" color="grey-900" pr="4xsmall">
              Colleagues to be invited
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              typography="caption-12"
              color="grey-900"
              backgroundColor="grey-50"
              borderRadius="50%"
              width="xsmall"
              height="xsmall"
            >
              {emailList.length}
            </Box>
          </Box>
          <Box display="flex" width="100%" pt="4xsmall" flexDirection="column">
            {emailList.map((email, index) => (
              <Box
                key={email}
                display="flex"
                width="100%"
                alignItems="center"
                pt={index > 0 ? '4xsmall' : '0'}
              >
                <Box flex="1" pr="tiny">
                  <Input value={email} readOnly label="" fullWidth />
                </Box>
                <ButtonIcon
                  baseColor="danger"
                  onClick={() =>
                    setEmailList((prevState) => prevState.filter((elem) => elem !== email))
                  }
                  round
                  size="medium"
                >
                  <IconDeleteOutline />
                </ButtonIcon>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default InviteTeam;
